import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"
import InvoiceLink from "src/main/Billing/Links/InvoiceLink"
import PaymentLink from "src/main/Billing/Links/PaymentLink"
import {
  INVOICE_ICON,
  PAYMENT_ICON,
  formatDay,
  formatMoment,
  methodLabel,
  methodReference,
} from "src/main/Billing/PaymentDetail/helpers"

import Timeline from "src/components/Timeline"

import { formatCurrencyFromMinorUnits } from "src/utils/UnitConversion"

import { useInvoiceTracker } from "./useInvoiceTracker"

const RETURN_ICON = "fa-regular fa-arrow-turn-left"
const FAILURE_ICON = "fa-regular fa-circle-exclamation"

// Item changes take no icon, so the timeline draws them as plain dots and the money stands out.
const KIND_ICONS = {
  invoice_created: INVOICE_ICON,
  item_returned_elsewhere: RETURN_ICON,
  item_returned_from: RETURN_ICON,
  payment: PAYMENT_ICON,
  payment_failed: FAILURE_ICON,
  refund: RETURN_ICON,
  refund_failed: FAILURE_ICON,
  credit: "fa-regular fa-tag",
}

const FILTERS = [
  { value: "all", label: "All" },
  {
    value: "payments",
    label: "Payments",
    matches: (item) => item.row.category === "money",
  },
  {
    value: "changes",
    label: "Changes",
    matches: (item) => item.row.category !== "money",
  },
]

const paymentMethod = (row) =>
  [methodLabel(row), methodReference(row)].filter(Boolean).join(" ")

const RelatedInvoice = ({ invoice }) =>
  invoice.detailPath ? (
    <InvoiceLink
      invoiceId={invoice.id}
      href={invoice.detailPath}
      className="font-semibold text-blue-600"
    >
      {invoice.displayId}
    </InvoiceLink>
  ) : (
    <span>{invoice.displayId}</span>
  )

RelatedInvoice.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayId: PropTypes.string.isRequired,
    detailPath: PropTypes.string,
  }).isRequired,
}

// The row links the earliest due of the other invoices and counts the rest.
const otherInvoices = (count) => {
  const more = count - 1
  if (more < 1) return null
  return ` and ${more} more ${more === 1 ? "invoice" : "invoices"}`
}

const PaymentNumber = ({ row }) =>
  row.paymentPath ? (
    <PaymentLink
      paymentTxnId={row.paymentTxnId}
      href={row.paymentPath}
      className="font-semibold text-blue-600"
    >
      {row.paymentDisplaySequentialId}
    </PaymentLink>
  ) : (
    <span>{row.paymentDisplaySequentialId}</span>
  )

PaymentNumber.propTypes = {
  row: PropTypes.shape({
    paymentTxnId: PropTypes.string,
    paymentPath: PropTypes.string,
    paymentDisplaySequentialId: PropTypes.string,
  }).isRequired,
}

const InvoiceActivity = ({ activity }) => {
  const { presentmentCurrency } = useMarina()
  const track = useInvoiceTracker()
  const money = (amount) =>
    formatCurrencyFromMinorUnits({ amount, currency: presentmentCurrency })

  const detail = (row) => {
    if (row.category === "money") {
      return [row.statusLabel, paymentMethod(row)].filter(Boolean).join(" · ")
    }
    if (row.kind === "due_date_changed") {
      return `${formatDay(row.previousDueOn)} → ${formatDay(row.newDueOn)}`
    }
    if (row.kind === "payment_method_changed") {
      return `${row.previousPaymentMethodLabel} → ${row.newPaymentMethodLabel}`
    }
    return row.itemName
  }

  const columns = [
    {
      key: "event",
      header: "Event",
      minWidth: "260px",
      render: ({ row }) => (
        <div>
          <span className="block font-semibold text-gray-900">
            <span>{row.kindLabel}</span>
            {row.relatedInvoice && (
              <>
                {" "}
                <RelatedInvoice invoice={row.relatedInvoice} />
                {otherInvoices(row.relatedInvoiceCount)}
              </>
            )}
            {row.paymentDisplaySequentialId && (
              <>
                {" "}
                <PaymentNumber row={row} />
              </>
            )}
          </span>
          {detail(row) && (
            <span className="block text-gray-700">{detail(row)}</span>
          )}
        </div>
      ),
    },
    {
      key: "date",
      header: "Date",
      width: "190px",
      render: ({ row }) => (
        <span className="whitespace-nowrap">
          {formatMoment(row.occurredAtLocal)}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      width: "130px",
      align: "right",
      render: ({ row }) =>
        row.amountCents === null ? (
          <>
            <span className="text-gray-400" aria-hidden="true">
              —
            </span>
            <span className="sr-only">Not applicable</span>
          </>
        ) : (
          money(row.amountCents)
        ),
    },
  ]

  return (
    <section aria-labelledby="invoice-activity-heading">
      <Timeline
        label="Invoice activity"
        heading={
          <h2
            id="invoice-activity-heading"
            className="m-0 flex items-center gap-2 text-base font-semibold text-gray-900"
          >
            <i
              className="fa-kit fa-invoice-activity text-gray-700"
              aria-hidden="true"
            />
            Invoice activity
          </h2>
        }
        columns={columns}
        items={activity.map((row) => ({
          id: row.id,
          icon: KIND_ICONS[row.kind],
          row,
        }))}
        filters={FILTERS}
        filterParam="activity"
        onFilterChange={(filter) =>
          track("activity_filter_pressed", { filter })
        }
      />
    </section>
  )
}

InvoiceActivity.propTypes = {
  activity: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      kind: PropTypes.string.isRequired,
      category: PropTypes.oneOf(["money", "item", "invoice_change"]).isRequired,
      kindLabel: PropTypes.string.isRequired,
      occurredAtLocal: PropTypes.string.isRequired,
      amountCents: PropTypes.number,
      itemName: PropTypes.string,
      previousDueOn: PropTypes.string,
      newDueOn: PropTypes.string,
      previousPaymentMethodLabel: PropTypes.string,
      newPaymentMethodLabel: PropTypes.string,
      statusLabel: PropTypes.string,
      paymentDisplaySequentialId: PropTypes.string,
      paymentTxnId: PropTypes.string,
      paymentPath: PropTypes.string,
      paymentKind: PropTypes.oneOf(["card", "offline"]),
      paymentMethodType: PropTypes.string,
      cardBrand: PropTypes.string,
      bankName: PropTypes.string,
      cardLast4: PropTypes.string,
      offlinePaymentType: PropTypes.string,
      checkNumber: PropTypes.string,
      achConfirmation: PropTypes.string,
      relatedInvoiceCount: PropTypes.number,
      relatedInvoice: PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayId: PropTypes.string.isRequired,
        detailPath: PropTypes.string,
      }),
    })
  ).isRequired,
}

export default InvoiceActivity
